/* CUSTOM_PLANNER_BY_CLAUDE — Floor plan background image tool */

class RoomDesignerBackground {
  constructor(rd) {
    this.rd = rd;

    // Multi-image array — each entry: {id, image, imageData, naturalWidth, naturalHeight, worldX, worldY, worldWidth, worldHeight, rotation, opacity, visible, scaleSet, pixelsPerFoot}
    this._bgImages = [];
    this._bgSelectedIdx = -1;       // index into _bgImages (-1 = none)
    this._nextBgId = 1;

    // Mirror properties for the SELECTED image (most code reads/writes these)
    this._bgImage = null;
    this._bgImageData = null;
    this._bgNaturalWidth = 0;
    this._bgNaturalHeight = 0;
    this._bgWorldX = 0;
    this._bgWorldY = 0;
    this._bgWorldWidth = 0;
    this._bgWorldHeight = 0;
    this._bgRotation = 0;
    this._bgOpacity = 1.0;
    this._bgVisible = true;
    this._bgScaleSet = false;
    this._bgPixelsPerFoot = 0;

    // Calibration transient state
    this._calibrating = false;
    this._leveling = false;
    this._calPoint1 = null;
    this._calPoint2 = null;
    this._calMouseWorld = null;
    this._calStep = 0;

    // Selection + drag transient state
    this._bgSelected = false;
    this._draggingBg = false;
    this._dragStartWorld = null;
    this._dragStartBgPos = null;

    // Flag on RD instance — render module checks this to skip room fill
    rd._bgTraceActive = false;

    this._createToolbarButton();
    this._createPopover();
    this._createCalibrationToast();
    this._installHook();
    this._wrapMouseHandlers();
    this._wrapToolSwitch();
    this._wrapBoxSelect();
    this._wrapUndoRedo();
    this._wrapDelete();
    this._wrapSerialization();
    this._setupKeyboard();

    // Cancel calibration when switching to Floor/Equipment/Finishing tabs
    const self = this;
    const overlay = window.roomDesignerOverlay;
    if (overlay && overlay._applyStepChange) {
      const origApplyStep = overlay._applyStepChange.bind(overlay);
      overlay._applyStepChange = function(step) {
        if (step !== 'draw' && self._calibrating) self._cancelCalibration();
        origApplyStep(step);
      };
    }

    // Rebuild cal input row when units change (so fields match new unit)
    document.querySelectorAll('.unit-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        if (self._calibrating && self._calStep === 3) {
          self._buildCalInputRow();
          const firstInput = document.getElementById('bgCalDist1');
          if (firstInput) firstInput.focus();
        }
      });
    });
  }

  // ─── Multi-Image Helpers ──────────────────────────────────────────

  // Save current _bg* mirror properties back into the array entry
  _saveCurrent() {
    if (this._bgSelectedIdx < 0 || this._bgSelectedIdx >= this._bgImages.length) return;
    const img = this._bgImages[this._bgSelectedIdx];
    if (this._bgImage) img.image = this._bgImage;
    img.imageData = this._bgImageData;
    img.naturalWidth = this._bgNaturalWidth;
    img.naturalHeight = this._bgNaturalHeight;
    img.worldX = this._bgWorldX;
    img.worldY = this._bgWorldY;
    img.worldWidth = this._bgWorldWidth;
    img.worldHeight = this._bgWorldHeight;
    img.rotation = this._bgRotation;
    img.opacity = this._bgOpacity;
    img.visible = this._bgVisible;
    img.scaleSet = this._bgScaleSet;
    img.pixelsPerFoot = this._bgPixelsPerFoot;
  }

  // Load array entry into _bg* mirror properties and select it
  _loadImage(idx) {
    this._bgSelectedIdx = idx;
    if (idx < 0 || idx >= this._bgImages.length) {
      this._bgSelectedIdx = -1;
      this._bgImage = null; this._bgImageData = null;
      this._bgNaturalWidth = 0; this._bgNaturalHeight = 0;
      this._bgWorldX = 0; this._bgWorldY = 0;
      this._bgWorldWidth = 0; this._bgWorldHeight = 0;
      this._bgRotation = 0; this._bgOpacity = 1.0; this._bgVisible = true;
      this._bgScaleSet = false; this._bgPixelsPerFoot = 0;
      return;
    }
    const img = this._bgImages[idx];
    this._bgImage = img.image;
    this._bgImageData = img.imageData;
    this._bgNaturalWidth = img.naturalWidth;
    this._bgNaturalHeight = img.naturalHeight;
    this._bgWorldX = img.worldX;
    this._bgWorldY = img.worldY;
    this._bgWorldWidth = img.worldWidth;
    this._bgWorldHeight = img.worldHeight;
    this._bgRotation = img.rotation;
    this._bgOpacity = img.opacity;
    this._bgVisible = img.visible;
    this._bgScaleSet = img.scaleSet;
    this._bgPixelsPerFoot = img.pixelsPerFoot;
  }

  // Hit-test all images in reverse order (topmost first), return index or -1
  _hitTestAny(wx, wy) {
    for (let i = this._bgImages.length - 1; i >= 0; i--) {
      const img = this._bgImages[i];
      if (!img.image || !img.visible) continue;
      const cx = img.worldX + img.worldWidth / 2;
      const cy = img.worldY + img.worldHeight / 2;
      const dx = wx - cx, dy = wy - cy;
      const cos = Math.cos(-img.rotation), sin = Math.sin(-img.rotation);
      const lx = dx * cos - dy * sin, ly = dx * sin + dy * cos;
      if (lx >= -img.worldWidth / 2 && lx <= img.worldWidth / 2 &&
          ly >= -img.worldHeight / 2 && ly <= img.worldHeight / 2) return i;
    }
    return -1;
  }

  // Update popover controls to reflect the selected image's properties
  _updatePopoverUI() {
    const slider = document.getElementById('bgOpacitySlider');
    if (slider) slider.value = Math.round(this._bgOpacity * 100);
    const opLabel = document.getElementById('bgOpacityLabel');
    if (opLabel) opLabel.textContent = Math.round(this._bgOpacity * 100) + '%';
    const rotSlider = document.getElementById('bgRotationSlider');
    const rotDegRaw = this._bgRotation * 180 / Math.PI;
    if (rotSlider) rotSlider.value = Math.round(rotDegRaw);
    const rotInput = document.getElementById('bgRotationInput');
    if (rotInput) rotInput.value = Math.round(rotDegRaw * 10) / 10;
    const visCheck = document.getElementById('bgVisibleCheckbox');
    if (visCheck) visCheck.checked = this._bgVisible;
    const statusEl = document.getElementById('bgScaleStatus');
    if (statusEl) statusEl.textContent = this._bgScaleSet ? 'Scale set' : '';
    // Image counter label
    this._updateImageCounter();
  }

  _updateImageCounter() {
    const el = document.getElementById('bgImageCounter');
    const nav = document.getElementById('bgImageNav');
    if (!el) return;
    const n = this._bgImages.length;
    if (n < 2) {
      if (nav) nav.style.display = 'none';
      return;
    }
    if (nav) nav.style.display = '';
    el.textContent = 'Image ' + (this._bgSelectedIdx + 1);
  }

  // ─── Toolbar Button ───────────────────────────────────────────────

  _createToolbarButton() {
    const rd = this.rd;
    const btn = document.createElement('button');
    btn.className = 'tool-btn';
    btn.id = 'bgImageBtn';
    btn.title = 'Trace Blueprint (B)';
    btn.innerHTML = `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" stroke-dasharray="3 2"/><polyline points="8 13 12 9 16 13"/><line x1="12" y1="9" x2="12" y2="17"/></svg>`;

    // Insert before zoom button
    const zoomBtn = document.getElementById('zoomToggleBtn');
    if (zoomBtn && zoomBtn.parentNode) {
      zoomBtn.parentNode.insertBefore(btn, zoomBtn);
    }

    // Hook into popover system
    const self = this;
    btn.addEventListener('mouseenter', () => rd._hoverShowPop('bgImagePopover'));
    btn.addEventListener('mouseleave', () => { rd._cancelHoverShow(); rd._hidePop('bgImagePopover'); });
    btn.addEventListener('click', () => {
      const wasOpen = rd._pinnedPopover === 'bgImage';
      if (!wasOpen && !self._calibrating) rd.setActiveTool('select'); // switch to select so canvas clicks interact with images, not draw
      rd._pinToggle('bgImage', 'bgImagePopover');
    });
  }

  // ─── Popover ──────────────────────────────────────────────────────

  _createPopover() {
    const rd = this.rd;
    const self = this;

    const pop = document.createElement('div');
    pop.className = 'pen-popover';
    pop.id = 'bgImagePopover';
    pop.style.cssText = 'width:210px;';
    pop.innerHTML = `
      <div class="popover-title-row">
        <div class="pen-popover-title">Trace Blueprint</div>
      </div>
      <div class="pop-group">
        <button class="pen-draw-mode-btn" id="bgUploadBtn" style="width:100%;justify-content:center;">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
          Upload Image
        </button>
        <input type="file" id="bgImageFileInput" accept="image/png,image/jpeg,application/pdf" style="display:none;">
      </div>
      <div id="bgControlsSection">
        <div class="pen-popover-divider"></div>
        <div class="bg-image-nav" id="bgImageNav" style="display:none;">
          <button class="bg-image-nav-prev" id="bgPrevBtn" title="Previous image">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <span class="bg-image-nav-label" id="bgImageCounter"></span>
          <button class="bg-image-nav-next" id="bgNextBtn" title="Next image">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 6 15 12 9 18"/></svg>
          </button>
        </div>
        <div class="pop-group">
          <span class="pop-label">Opacity</span>
          <div style="display:flex;align-items:center;gap:6px;">
            <input type="range" id="bgOpacitySlider" min="10" max="100" value="100" style="flex:1;accent-color:#006BCC;">
            <span id="bgOpacityLabel" class="pop-label" style="font-size:12px;min-width:36px;text-align:right;">100%</span>
          </div>
          <label class="pop-label" style="display:flex;align-items:center;gap:4px;font-size:12px;font-weight:normal;cursor:pointer;margin-top:4px;">
            <input type="checkbox" id="bgVisibleCheckbox" checked> Show image
          </label>
        </div>
        <div class="pop-group">
          <span class="pop-label">Rotation</span>
          <div style="display:flex;align-items:center;gap:5px;">
            <input type="range" id="bgRotationSlider" min="0" max="360" value="0" step="1" style="flex:1;accent-color:#006BCC;">
            <input type="number" id="bgRotationInput" min="0" max="360" step="0.1" value="0" style="width:50px;font-size:12px;font-weight:600;text-align:center;padding:2px 2px 2px 6px;border:1px solid #ccc;border-radius:4px;">
            <span class="pop-label" style="font-size:12px;margin-left:-2px;">°</span>
          </div>
        </div>
        <div class="pop-group">
          <span class="pop-label">Level</span>
          <button class="pen-draw-mode-btn" id="bgLevelBtn" style="width:100%;justify-content:center;">
            <svg width="18" height="18" viewBox="-2 0 28 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="-1" y1="18" x2="25" y2="18"/><path d="M5 18 A7 7 0 0 1 19 18"/></svg>
            Level
          </button>
        </div>
        <div class="pop-group">
          <span class="pop-label">Scale</span>
          <button class="pen-draw-mode-btn" id="bgSetScaleBtn" style="width:100%;justify-content:center;">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="8" width="22" height="8" rx="1"/><line x1="6" y1="8" x2="6" y2="12"/><line x1="10" y1="8" x2="10" y2="11"/><line x1="14" y1="8" x2="14" y2="12"/><line x1="18" y1="8" x2="18" y2="11"/></svg>
            Set Scale
          </button>
          <span id="bgScaleStatus" style="font-size:11px;color:#666;width:100%;"></span>
          <!-- CUSTOM_ROOMDESIGNER_BY_CLAUDE — Set Scale offers a choice of method
               rather than starting the two-point flow directly. The area route is for
               drawings with no dimension line to trace; it is immune to crop, resize
               and DPI, which the printed-scale note is not. -->
          <!-- Must be a flex COLUMN: .pen-popover sets white-space:nowrap with
               overflow:hidden, so inline-block buttons in a plain div sit on one line
               and the second gets clipped off the panel. Toggled to 'flex', never ''. -->
          <div id="bgScaleMethods" style="display:none;flex-direction:column;gap:4px;width:100%;margin-top:4px;white-space:normal;">
            <button class="pen-draw-mode-btn" id="bgScaleByDistBtn" style="width:100%;justify-content:center;">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="8" x2="3" y2="16"/><line x1="21" y1="8" x2="21" y2="16"/></svg>
              By a measurement
            </button>
            <button class="pen-draw-mode-btn" id="bgScaleByAreaBtn" style="width:100%;justify-content:center;">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="16" rx="1"/><path d="M3 15c4-5 8-5 12 0"/></svg>
              By room area
            </button>
          </div>
        </div>
        <div id="bgCalInputSection" style="display:none;">
          <div class="pop-group">
            <span class="pop-label" id="bgCalInputLabel">Distance between points</span>
            <div id="bgCalInputRow" style="display:flex;gap:4px;align-items:center;"></div>
          </div>
        </div>
        <!-- CUSTOM_ROOMDESIGNER_BY_CLAUDE — independent check against the sheet's printed
             scale. Its real job is catching a CROPPED image, which the area method can't
             detect on its own. Optional: needs the sheet size to mean anything. -->
        <div id="bgPaperCheckSection" style="display:none;">
          <div class="pen-popover-divider"></div>
          <div class="pop-group">
            <span class="pop-label">Cross-check vs printed scale</span>
            <!-- display:block for the same reason as the method buttons: .pen-popover
                 is white-space:nowrap, so two 100%-wide inline-block selects would
                 share a line and the second would be clipped off the panel. -->
            <select id="bgPaperScale" style="display:block;width:100%;font-size:12px;padding:4px 6px;border-radius:4px;height:28px;margin-bottom:4px;">
              <option value="">Drawing scale…</option>
              <option value="0.0625">1/16" = 1'-0"</option>
              <option value="0.09375">3/32" = 1'-0"</option>
              <option value="0.125">1/8" = 1'-0"</option>
              <option value="0.1875">3/16" = 1'-0"</option>
              <option value="0.25">1/4" = 1'-0"</option>
              <option value="0.375">3/8" = 1'-0"</option>
              <option value="0.5">1/2" = 1'-0"</option>
              <option value="1">1" = 1'-0"</option>
            </select>
            <select id="bgPaperSheet" style="display:block;width:100%;font-size:12px;padding:4px 6px;border-radius:4px;height:28px;">
              <option value="">Sheet size…</option>
              <option value="36">ARCH D — 24 × 36 in</option>
              <option value="42">ARCH E1 — 30 × 42 in</option>
              <option value="48">ARCH E — 36 × 48 in</option>
              <option value="17">ANSI B — 11 × 17 in</option>
              <option value="22">ANSI C — 17 × 22 in</option>
              <option value="34">ANSI D — 22 × 34 in</option>
            </select>
            <span id="bgPaperCheckResult" style="display:block;font-size:11px;width:100%;margin-top:4px;line-height:1.4;white-space:normal;"></span>
          </div>
        </div>
      </div>
    `;

    // Insert popover into toolbar container
    const toolbar = document.querySelector('.tool-controls');
    if (toolbar) toolbar.appendChild(pop);

    // Popover hover events
    pop.addEventListener('mouseenter', () => rd._showPop('bgImagePopover'));
    pop.addEventListener('mouseleave', () => rd._hidePop('bgImagePopover'));

    // Wire up events
    const fileInput = document.getElementById('bgImageFileInput');
    document.getElementById('bgUploadBtn').addEventListener('click', () => fileInput.click());
    fileInput.addEventListener('change', (e) => this._handleFileSelect(e));

    // Prev/Next image navigation
    document.getElementById('bgPrevBtn').addEventListener('click', () => {
      if (this._bgImages.length <= 1) return;
      this._saveCurrent();
      const newIdx = (this._bgSelectedIdx - 1 + this._bgImages.length) % this._bgImages.length;
      this._loadImage(newIdx);
      this._updatePopoverUI();
      this._bgSelected = true;
      rd.render();
    });
    document.getElementById('bgNextBtn').addEventListener('click', () => {
      if (this._bgImages.length <= 1) return;
      this._saveCurrent();
      const newIdx = (this._bgSelectedIdx + 1) % this._bgImages.length;
      this._loadImage(newIdx);
      this._updatePopoverUI();
      this._bgSelected = true;
      rd.render();
    });

    document.getElementById('bgOpacitySlider').addEventListener('input', (e) => {
      this._bgOpacity = parseInt(e.target.value) / 100;
      document.getElementById('bgOpacityLabel').textContent = e.target.value + '%';
      this._saveCurrent();
      rd.render();
    });

    const rotSlider = document.getElementById('bgRotationSlider');
    const rotInput = document.getElementById('bgRotationInput');
    rotSlider.addEventListener('input', (e) => {
      const deg = parseFloat(e.target.value) || 0;
      this._bgRotation = deg * Math.PI / 180;
      rotInput.value = deg;
      this._saveCurrent();
      rd.render();
    });
    rotInput.addEventListener('input', (e) => {
      let deg = parseFloat(e.target.value) || 0;
      if (deg < 0) deg = 0;
      if (deg > 360) deg = 360;
      this._bgRotation = deg * Math.PI / 180;
      rotSlider.value = deg;
      this._saveCurrent();
      rd.render();
    });

    // CUSTOM_ROOMDESIGNER_BY_CLAUDE — Set Scale now reveals the method chooser
    document.getElementById('bgSetScaleBtn').addEventListener('click', () => this._toggleScaleMethods());
    const distBtn = document.getElementById('bgScaleByDistBtn');
    if (distBtn) distBtn.addEventListener('click', () => this._startCalibration());
    const areaBtn = document.getElementById('bgScaleByAreaBtn');
    if (areaBtn) areaBtn.addEventListener('click', () => this._startAreaCalibration());
    ['bgPaperScale', 'bgPaperSheet'].forEach(id => {
      const el = document.getElementById(id);
      if (el) el.addEventListener('change', () => this._updatePaperCheck());
    });
    // END CUSTOM_ROOMDESIGNER_BY_CLAUDE
    document.getElementById('bgLevelBtn').addEventListener('click', () => this._startLevel());

    document.getElementById('bgVisibleCheckbox').addEventListener('change', (e) => {
      this._bgVisible = e.target.checked;
      this._saveCurrent();
      this._syncTraceFlag();
      rd.render();
    });

    // Start with controls grayed out (no image yet)
    this._showControls(false);
  }

  // ─── File Upload ──────────────────────────────────────────────────

  _handleFileSelect(e) {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = ''; // reset so same file can be re-selected

    // File size cap: 10 MB
    const MAX_FILE_MB = 10;
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
      alert('Image is too large (' + sizeMB + ' MB). Please use an image under ' + MAX_FILE_MB + ' MB.');
      return;
    }

    if (file.type === 'application/pdf') {
      this._loadPDF(file);
    } else {
      this._loadImageFile(file);
    }
  }

  _loadImageFile(file) {
    const reader = new FileReader();
    reader.onload = (e) => this._setImageFromDataURL(e.target.result);
    reader.readAsDataURL(file);
  }

  _setImageFromDataURL(dataURL) {
    const self = this;
    const rd = this.rd;

    const img = new Image();
    img.onload = () => {
      let w = img.naturalWidth, h = img.naturalHeight;
      const MAX_DIM = 4000;
      const needsResize = w > MAX_DIM || h > MAX_DIM;

      let finalDataURL;
      if (needsResize) {
        // Downscale oversized images, re-encode as JPEG 0.9
        const ratio = Math.min(MAX_DIM / w, MAX_DIM / h);
        w = Math.round(w * ratio);
        h = Math.round(h * ratio);
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        finalDataURL = canvas.toDataURL('image/jpeg', 0.9);
      } else {
        // Keep original quality — no re-encoding
        finalDataURL = dataURL;
      }

      // Save current selection before adding new image
      self._saveCurrent();

      const loadedImg = new Image();
      loadedImg.onload = () => {
        // Create new array entry
        const entry = {
          id: self._nextBgId++,
          image: loadedImg,
          imageData: finalDataURL,
          naturalWidth: w,
          naturalHeight: h,
          worldX: 0, worldY: 0, worldWidth: 0, worldHeight: 0,
          rotation: 0, opacity: 1.0, visible: true,
          scaleSet: false, pixelsPerFoot: 0,
          // CUSTOM_ROOMDESIGNER_BY_CLAUDE — true sheet size in inches, read from the
          // PDF page. Undefined for raster uploads, where nothing in the file can be
          // trusted to say how big the sheet was.
          sheetIn: self._pendingSheetIn || null
        };
        self._pendingSheetIn = null;
        rd.saveUndoState();
        self._bgImages.push(entry);
        self._loadImage(self._bgImages.length - 1);
        self._bgSelected = true;
        self._calibrating = false;
        self._leveling = false;
        self._calStep = 0;
        self._calPoint1 = null;
        self._calPoint2 = null;

        // Auto-fit: fill ~80% of visible canvas area
        self._autoFitImage();
        self._saveCurrent(); // persist auto-fit position to array
        self._showControls(true);
        self._updatePopoverUI();
        self._syncTraceFlag();
        if (rd._updateFloatingDelete) rd._updateFloatingDelete();
        rd.render();
      };
      loadedImg.src = finalDataURL;
    };
    img.src = dataURL;
  }

  _autoFitImage() {
    const rd = this.rd;
    const canvas = rd.canvas;
    // Visible world rect
    const worldW = canvas.width / (rd.basePixelsPerFoot * rd.zoomLevel * devicePixelRatio);
    const worldH = canvas.height / (rd.basePixelsPerFoot * rd.zoomLevel * devicePixelRatio);
    const _cw = rd.screenToWorld(rd.canvas.width / (2 * devicePixelRatio), rd.canvas.height / (2 * devicePixelRatio));
    const centerWX = _cw.x;
    const centerWY = _cw.y;

    // Default: assume 96 DPI → 1 pixel = 1/96 inch = 1/1152 ft
    // This gives a reasonable starting size for most floor plans
    const defaultPPF = 96 * 12; // pixels per foot at 96 DPI
    let imgWorldW = this._bgNaturalWidth / defaultPPF;
    let imgWorldH = this._bgNaturalHeight / defaultPPF;

    // Scale to fit 80% of view
    const targetW = worldW * 0.8;
    const targetH = worldH * 0.8;
    const fitScale = Math.min(targetW / imgWorldW, targetH / imgWorldH);
    imgWorldW *= fitScale;
    imgWorldH *= fitScale;

    this._bgWorldWidth = imgWorldW;
    this._bgWorldHeight = imgWorldH;
    this._bgWorldX = centerWX - imgWorldW / 2;
    this._bgWorldY = centerWY - imgWorldH / 2;
  }

  // ─── PDF Support ──────────────────────────────────────────────────

  _loadPDF(file) {
    const self = this;
    const statusEl = document.getElementById('bgScaleStatus');
    if (statusEl) statusEl.textContent = 'Loading PDF...';

    // Lazy-load pdf.js from CDN
    if (window.pdfjsLib) {
      this._renderPDF(file);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    script.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      self._renderPDF(file);
    };
    script.onerror = () => {
      if (statusEl) statusEl.textContent = 'Failed to load PDF library';
    };
    document.head.appendChild(script);
  }

  _renderPDF(file) {
    const self = this;
    const statusEl = document.getElementById('bgScaleStatus');
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const pdfData = new Uint8Array(e.target.result);
        const pdf = await window.pdfjsLib.getDocument({ data: pdfData }).promise;
        if (pdf.numPages > 1) {
          // Multi-page PDF — show page picker
          self._showPdfPagePicker(pdf);
        } else {
          // Single page — render directly
          await self._addPdfPage(pdf, 1);
        }
        if (statusEl) statusEl.textContent = '';
      } catch (err) {
        console.error('PDF render error:', err);
        if (statusEl) statusEl.textContent = 'Failed to render PDF';
      }
    };
    reader.readAsArrayBuffer(file);
  }

  async _addPdfPage(pdf, pageNum) {
    const page = await pdf.getPage(pageNum);
    const viewport = page.getViewport({ scale: 2.0 });
    // CUSTOM_ROOMDESIGNER_BY_CLAUDE — a PDF states its own page size, so the sheet
    // dimensions don't have to be guessed. getViewport({scale:1}) is in POINTS
    // (1/72in), which makes the printed-scale cross-check exact for PDFs — and
    // immune to the crop problem, since we're reading the page, not assuming it.
    const pt = page.getViewport({ scale: 1.0 });
    this._pendingSheetIn = {
      w: pt.width / 72,
      h: pt.height / 72,
      rotation: pt.rotation || 0
    };
    // END CUSTOM_ROOMDESIGNER_BY_CLAUDE
    const canvas = document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const ctx = canvas.getContext('2d');
    await page.render({ canvasContext: ctx, viewport }).promise;
    const dataURL = canvas.toDataURL('image/png');
    this._setImageFromDataURL(dataURL);
  }

  _showPdfPagePicker(pdf) {
    const self = this;
    const numPages = Math.min(pdf.numPages, 50); // cap thumbnails
    const selected = new Set();

    // Build overlay
    const overlay = document.createElement('div');
    overlay.className = 'rd-pdf-picker-overlay';
    const isDark = this.rd.darkMode;
    if (isDark) overlay.classList.add('dark');

    const dialog = document.createElement('div');
    dialog.className = 'rd-pdf-picker-dialog';

    const title = document.createElement('div');
    title.className = 'rd-pdf-picker-title';
    title.textContent = 'Select PDF Pages';

    const subtitle = document.createElement('div');
    subtitle.className = 'rd-pdf-picker-subtitle';
    subtitle.textContent = pdf.numPages + ' pages found — click to select';

    const grid = document.createElement('div');
    grid.className = 'rd-pdf-picker-grid';

    const actions = document.createElement('div');
    actions.className = 'rd-pdf-picker-actions';

    const cancelBtn = document.createElement('button');
    cancelBtn.className = 'rd-pdf-picker-btn rd-pdf-picker-cancel';
    cancelBtn.textContent = 'Cancel';

    const addBtn = document.createElement('button');
    addBtn.className = 'rd-pdf-picker-btn rd-pdf-picker-add';
    addBtn.textContent = 'Add Selected';
    addBtn.disabled = true;

    actions.appendChild(cancelBtn);
    actions.appendChild(addBtn);
    dialog.appendChild(title);
    dialog.appendChild(subtitle);
    dialog.appendChild(grid);
    dialog.appendChild(actions);
    overlay.appendChild(dialog);

    // Find the room-designer container (so z-index is scoped)
    const container = this.rd.canvas.closest('.room-designer') || document.body;
    container.appendChild(overlay);

    // Animate in
    requestAnimationFrame(() => overlay.classList.add('visible'));

    const updateAddBtn = () => {
      addBtn.disabled = selected.size === 0;
      addBtn.textContent = selected.size > 0 ? 'Add ' + selected.size + ' Page' + (selected.size > 1 ? 's' : '') : 'Add Selected';
    };

    // Render thumbnails
    for (let i = 1; i <= numPages; i++) {
      const cell = document.createElement('div');
      cell.className = 'rd-pdf-picker-cell';
      cell.dataset.page = i;

      const thumb = document.createElement('div');
      thumb.className = 'rd-pdf-picker-thumb';

      // Spinner placeholder
      const spinner = document.createElement('div');
      spinner.className = 'rd-pdf-picker-spinner';
      thumb.appendChild(spinner);

      const label = document.createElement('div');
      label.className = 'rd-pdf-picker-label';
      label.textContent = 'Page ' + i;

      const check = document.createElement('div');
      check.className = 'rd-pdf-picker-check';
      check.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>';

      cell.appendChild(thumb);
      cell.appendChild(check);
      cell.appendChild(label);
      grid.appendChild(cell);

      // Toggle selection on click
      cell.addEventListener('click', () => {
        if (selected.has(i)) {
          selected.delete(i);
          cell.classList.remove('selected');
        } else {
          selected.add(i);
          cell.classList.add('selected');
        }
        updateAddBtn();
      });

      // Render thumbnail async
      (async (pageNum, thumbEl, spinnerEl) => {
        try {
          const page = await pdf.getPage(pageNum);
          const vp = page.getViewport({ scale: 1.0 });
          const thumbScale = Math.min(180 / vp.width, 240 / vp.height);
          const tvp = page.getViewport({ scale: thumbScale });
          const c = document.createElement('canvas');
          c.width = tvp.width;
          c.height = tvp.height;
          const cx = c.getContext('2d');
          await page.render({ canvasContext: cx, viewport: tvp }).promise;
          c.style.cssText = 'width:100%;height:100%;object-fit:contain;display:block;';
          spinnerEl.remove();
          thumbEl.appendChild(c);
        } catch (err) {
          spinnerEl.textContent = '!';
          console.error('PDF thumbnail error page ' + pageNum, err);
        }
      })(i, thumb, spinner);
    }

    // Close helper
    const close = () => {
      overlay.classList.remove('visible');
      setTimeout(() => overlay.remove(), 200);
    };

    cancelBtn.addEventListener('click', close);

    // Click overlay background to cancel
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) close();
    });

    addBtn.addEventListener('click', async () => {
      if (selected.size === 0) return;
      const pages = Array.from(selected).sort((a, b) => a - b);
      close();
      const statusEl = document.getElementById('bgScaleStatus');
      for (let idx = 0; idx < pages.length; idx++) {
        if (statusEl) statusEl.textContent = 'Rendering page ' + pages[idx] + '...';
        await self._addPdfPage(pdf, pages[idx]);
      }
      if (statusEl) statusEl.textContent = '';
    });
  }

  // ─── UI Helpers ───────────────────────────────────────────────────

  _showControls(enabled) {
    const section = document.getElementById('bgControlsSection');
    if (!section) return;
    // Always visible — toggle disabled/grayed state
    const ids = ['bgOpacitySlider', 'bgVisibleCheckbox', 'bgRotationSlider', 'bgRotationInput',
                 'bgLevelBtn', 'bgSetScaleBtn', 'bgPrevBtn', 'bgNextBtn'];
    ids.forEach(id => {
      const el = document.getElementById(id);
      if (el) el.disabled = !enabled;
    });
    section.style.opacity = enabled ? '' : '0.4';
    section.style.pointerEvents = enabled ? '' : 'none';
  }

  _syncTraceFlag() {
    this.rd._bgTraceActive = !this.rd._viewOnly && this._bgImages.some(img => img.image && img.visible);
  }

  _clearCalBtnActive() {
    const lvl = document.getElementById('bgLevelBtn');
    const scl = document.getElementById('bgSetScaleBtn');
    if (lvl) lvl.classList.remove('active');
    if (scl) scl.classList.remove('active');
  }

  // ─── Calibration Toast (on-canvas instruction) ─────────────────

  _createCalibrationToast() {
    const rd = this.rd;
    const wrapper = rd.canvas.parentElement;
    const toast = document.createElement('div');
    toast.id = 'bgCalToast';
    toast.style.cssText = `
      position:absolute; top:16px; left:50%; transform:translateX(-50%);
      background:rgba(0,15,28,0.88); color:#fff; padding:10px 20px;
      border-radius:10px; font-size:14px; font-weight:600; z-index:60;
      pointer-events:none; opacity:0; transition:opacity 0.2s ease;
      white-space:nowrap; box-shadow:0 4px 16px rgba(0,0,0,0.25);
      backdrop-filter:blur(6px); -webkit-backdrop-filter:blur(6px);
    `;
    toast.innerHTML = '<span id="bgCalToastStep" style="color:#5BA4E6;margin-right:6px;">Step 1:</span><span id="bgCalToastText"></span>';
    wrapper.appendChild(toast);
  }

  _showCalToast(step, text) {
    const toast = document.getElementById('bgCalToast');
    if (!toast) return;
    const stepEl = document.getElementById('bgCalToastStep');
    const textEl = document.getElementById('bgCalToastText');
    if (stepEl) stepEl.textContent = step ? 'Step ' + step + ':' : '';
    if (textEl) textEl.textContent = text;
    toast.style.opacity = '1';
  }

  _hideCalToast() {
    const toast = document.getElementById('bgCalToast');
    if (toast) toast.style.opacity = '0';
  }

  _deselectBg() {
    if (!this._bgSelected) return;
    this._bgSelected = false;
    this._draggingBg = false;
    this._dragStartWorld = null;
    this._dragStartBgPos = null;
    if (this.rd._updateFloatingDelete) this.rd._updateFloatingDelete();
    this.rd.render();
  }

  _removeImage() {
    // Remove selected image from array
    if (this._bgSelectedIdx >= 0 && this._bgSelectedIdx < this._bgImages.length) {
      this._bgImages.splice(this._bgSelectedIdx, 1);
    }

    this._calibrating = false;
    this._leveling = false;
    this._calStep = 0;
    this._calPoint1 = null;
    this._calPoint2 = null;
    this._draggingBg = false;
    this._bgSelected = false;

    if (this._bgImages.length > 0) {
      // Select the previous image (or last if we removed the first)
      const newIdx = Math.min(this._bgSelectedIdx, this._bgImages.length - 1);
      this._loadImage(newIdx);
      this._updatePopoverUI();
      this._showControls(true);
    } else {
      // No images left
      this._loadImage(-1);
      this._showControls(false);
      const slider = document.getElementById('bgOpacitySlider');
      if (slider) { slider.value = 100; }
      const opLabel = document.getElementById('bgOpacityLabel');
      if (opLabel) opLabel.textContent = '100%';
      const rotSlider = document.getElementById('bgRotationSlider');
      if (rotSlider) rotSlider.value = 0;
      const rotInput2 = document.getElementById('bgRotationInput');
      if (rotInput2) rotInput2.value = 0;
      const visCheck = document.getElementById('bgVisibleCheckbox');
      if (visCheck) visCheck.checked = true;
      const statusEl = document.getElementById('bgScaleStatus');
      if (statusEl) statusEl.textContent = '';
    }
    const calSection = document.getElementById('bgCalInputSection');
    if (calSection) calSection.style.display = 'none';
    this._hideCalToast();

    this._syncTraceFlag();
    this.rd.render();
  }

  // ─── Calibration ──────────────────────────────────────────────────

  /**
   * CUSTOM_ROOMDESIGNER_BY_CLAUDE
   * Calibrate from a room's stated square footage, for drawings that carry room areas
   * but no dimension line to trace.
   *
   *     scaleFactor = sqrt(statedArea / tracedArea)
   *
   * Unlike the printed-scale notation this needs no sheet size and is immune to the
   * image being cropped, resized or exported at any DPI. The square root also halves
   * the error: a 450 SF room rounded to the nearest SF is +/-0.11% of area but only
   * +/-0.06% of linear scale.
   *
   * Uses the SAME two-click interaction as distance calibration — the two clicks are
   * opposite corners of a rectangular room rather than the ends of a dimension. Pick
   * the largest rectangular room on the sheet; a small one magnifies the rounding.
   *
   * Convention note: published areas are net interior, and GLD draws wall thickness
   * OUTWARD from the traced line, so the polygon you draw is the inside face. The two
   * agree — no half-wall offset to correct for.
   */
  /**
   * CUSTOM_ROOMDESIGNER_BY_CLAUDE
   * Independent scale derived from the sheet's printed scale notation, compared
   * against whatever calibration is actually in force.
   *
   *   pxPerPrintedInch = imageWidthPx / sheetWidthInches
   *   pxPerFoot        = pxPerPrintedInch x printedInchesPerFoot
   *
   * This is a CHECK, not a calibration method. The notation alone can't set a scale
   * because a digital image has pixels, not printed inches — and if the image has
   * been cropped, the sheet width no longer maps to the image width and the result
   * is confidently wrong with nothing in the image to reveal it. Which is exactly
   * why it earns its place as a cross-check: disagreement is the crop detector.
   * Resizing is harmless, since the ratio is taken from the current image width.
   */
  _updatePaperCheck() {
    const section = document.getElementById('bgPaperCheckSection');
    const out = document.getElementById('bgPaperCheckResult');
    if (!section || !out) return;
    section.style.display = this._bgScaleSet ? '' : 'none';
    if (!this._bgScaleSet) return;

    const sc = parseFloat(document.getElementById('bgPaperScale')?.value);
    // CUSTOM_ROOMDESIGNER_BY_CLAUDE — a PDF tells us its own page size, so the sheet
    // dropdown is only needed for raster uploads. Auto beats guessing, and it also
    // removes the crop ambiguity: the page size came from the file, not an assumption.
    const auto = this._autoSheetWidthIn();
    const picked = parseFloat(document.getElementById('bgPaperSheet')?.value);
    const sheetW = picked || auto;
    const sheetNote = picked ? '' : (auto ? ` (sheet ${this._fmtIn(auto)} from the PDF)` : '');
    const sel = document.getElementById('bgPaperSheet');
    if (sel && auto && !picked) {
      const first = sel.querySelector ? sel.querySelector('option') : null;
      if (first) first.textContent = `Auto — ${this._fmtIn(auto)} wide (from PDF)`;
    }
    if (!sc || !sheetW || !this._bgNaturalWidth) {
      out.style.color = '#666';
      out.textContent = auto
        ? 'Pick the drawing scale to check it against the sheet.'
        : 'Pick both to check the current scale against the sheet.';
      return;
    }
    const paperPxPerFoot = (this._bgNaturalWidth / sheetW) * sc;
    const actualPxPerFoot = this._bgPixelsPerFoot;
    if (!(paperPxPerFoot > 0) || !(actualPxPerFoot > 0)) return;
    const diff = (actualPxPerFoot - paperPxPerFoot) / paperPxPerFoot * 100;
    const mag = Math.abs(diff);
    const fmt = (n) => (Math.round(n * 100) / 100).toFixed(2);
    if (mag < 1) {
      out.style.color = '#0a7d28';
      out.textContent = `Agrees within ${fmt(mag)}% — scale confirmed.${sheetNote}`;
    } else if (mag < 5) {
      out.style.color = '#9a6700';
      out.textContent = `Differs by ${fmt(mag)}%. Close, but check the room area you used.${sheetNote}`;
    } else {
      out.style.color = '#CC0000';
      out.textContent = `Differs by ${fmt(mag)}% — ` +
        (picked ? 'the image is probably cropped, or the sheet size is wrong. '
                : 'the page is probably cropped or the drawing scale is not what was picked. ') +
        `Trust the room-area scale, not the sheet.${sheetNote}`;
    }
  }

  /**
   * Sheet width in inches when we can know it for certain — i.e. from a PDF page.
   * Honours page rotation, since a rotated page swaps width and height.
   * Returns 0 for raster uploads: PNG pHYs and JPEG density fields are almost always
   * a nominal 72/96 rather than the real sheet, so trusting them would be worse than
   * asking.
   */
  _autoSheetWidthIn() {
    const e = this._bgImages && this._bgImages[this._bgSelectedIdx];
    const s = e && e.sheetIn;
    if (!s || !(s.w > 0) || !(s.h > 0)) return 0;
    const rot = ((s.rotation || 0) % 360 + 360) % 360;
    return (rot === 90 || rot === 270) ? s.h : s.w;
  }

  _fmtIn(v) {
    const r = Math.round(v * 100) / 100;
    return (Math.abs(r - Math.round(r)) < 0.01 ? String(Math.round(r)) : r.toFixed(2)) + ' in';
  }

  /** Show/hide the Set Scale method chooser. CUSTOM_ROOMDESIGNER_BY_CLAUDE */
  _toggleScaleMethods() {
    if (!this._bgImage) return;
    if (this._calibrating) { this._cancelCalibration(); return; }
    const box = document.getElementById('bgScaleMethods');
    if (!box) return;
    const showing = box.style.display !== 'none';
    // 'flex', not '' — clearing the inline style would fall back to display:block and
    // the buttons would go back to sharing a nowrap line.
    box.style.display = showing ? 'none' : 'flex';
    const btn = document.getElementById('bgSetScaleBtn');
    if (btn) btn.classList.toggle('active', !showing);
  }

  _hideScaleMethods() {
    const box = document.getElementById('bgScaleMethods');
    if (box) box.style.display = 'none';
    const btn = document.getElementById('bgSetScaleBtn');
    if (btn) btn.classList.remove('active');
  }

  _startAreaCalibration() {
    if (!this._bgImage) return;
    this._calibrating = true;
    this._calMode = 'area';
    this._calStep = 1;
    this._calPoint1 = null;
    this._calPoint2 = null;
    this._calMouseWorld = null;
    this._calPoly = [];          // [{wx, wy, sag}] — sag bows the edge LEAVING this vertex
    this._calPolyClosed = false;
    this._calBowDrag = null;
    this._deselectBg();
    this.rd.canvas.style.cursor = 'crosshair';
    this._clearCalBtnActive();
    this._hideScaleMethods();
    const statusEl = document.getElementById('bgScaleStatus');
    if (statusEl) statusEl.textContent = '';
    const calSection = document.getElementById('bgCalInputSection');
    if (calSection) calSection.style.display = 'none';
    this._showCalToast(1, 'Trace the room — click each corner. Click the first point to close. Backspace undoes.');
    this.rd.render();
  }

  /** World-space radius that reads as a constant handful of screen pixels. */
  _calScreenR(px) {
    const scale = this.rd.basePixelsPerFoot * this.rd.zoomLevel;
    return px / (scale || 1);
  }

  /**
   * How close to the first vertex counts as "closing the loop".
   * CUSTOM_ROOMDESIGNER_BY_CLAUDE
   *
   * Uses the pen tool's own rule verbatim — 1in once you're zoomed past 3 screen
   * pixels per inch, 4in below that — so closing a traced outline feels identical to
   * closing a room freehand. Sharing the rule rather than inventing a second one
   * means the two can't drift apart later.
   */
  _calCloseRadius() {
    const screenPixelsPerInch = this.rd.basePixelsPerFoot * this.rd.zoomLevel / 12;
    return screenPixelsPerInch >= 3 ? (1 / 12) : (4 / 12);
  }

  /** Index of a traced vertex under the cursor, or -1. */
  _hitTestCalVertex(wx, wy) {
    const poly = this._calPoly;
    if (!poly || !poly.length) return -1;
    const r = this._calScreenR(9);
    for (let i = 0; i < poly.length; i++) {
      if (Math.hypot(wx - poly[i].wx, wy - poly[i].wy) <= r) return i;
    }
    return -1;
  }

  /** Midpoint handle for the bow on edge i of the traced outline. */
  _calBowHandle(i) {
    const CV = window.RoomDesignerCurve;
    const poly = this._calPoly;
    if (!CV || !poly || poly.length < 3) return null;
    const p = poly[i], q = poly[(i + 1) % poly.length];
    const pt = CV.midHandlePoint({ x: p.wx, y: p.wy }, { x: q.wx, y: q.wy }, p.sag || 0);
    return { x: pt.x, y: pt.y, i: i };
  }

  /**
   * Clicks while tracing the calibration outline.
   * Returns true if the click was consumed.
   */
  _handleAreaTraceClick(wx, wy) {
    const poly = this._calPoly;
    if (this._calPolyClosed) return false;   // closed: clicks fall through to bow drags
    const closeR = this._calCloseRadius();
    if (poly.length >= 3) {
      const first = poly[0];
      if (Math.hypot(wx - first.wx, wy - first.wy) <= closeR) {
        this._closeAreaTrace();
        return true;
      }
    }
    poly.push({ wx: wx, wy: wy, sag: 0 });
    this._showCalToast(1, poly.length < 3
      ? 'Keep clicking corners…'
      : 'Click the first point to close, or keep adding corners');
    this.rd.render();
    return true;
  }

  _closeAreaTrace() {
    if (!this._calPoly || this._calPoly.length < 3) return;
    this._calPolyClosed = true;
    this._calMouseWorld = null;
    this.rd.canvas.style.cursor = 'default';
    this._calStep = 3;
    this._showCalToast(3, 'Drag a wall to bow it, then enter the room area in the popover');
    this._buildCalInputRow();
    const calSection = document.getElementById('bgCalInputSection');
    if (calSection) calSection.style.display = '';
    this.rd.render();
  }

  /**
   * Render the traced calibration outline: vertices, edges (arc-tessellated where
   * bowed), the rubber-band to the cursor while open, and a bow handle per edge once
   * closed. Uses the same white-halo-under-blue idiom as the crosshair markers so it
   * stays legible over any blueprint.
   */
  _drawCalTrace(ctx, scale, blue, white) {
    const CV = window.RoomDesignerCurve;
    const poly = this._calPoly;
    const outerW = 3.5 / scale, innerW = 1.6 / scale;
    const dotR = 4 / scale;

    // Edge path, following each bow.
    const edgePath = (closed) => {
      const path = new Path2D();
      const n = poly.length;
      const last = closed ? n : n - 1;
      path.moveTo(poly[0].wx, poly[0].wy);
      for (let i = 0; i < last; i++) {
        const p = poly[i], q = poly[(i + 1) % n];
        const a = { x: p.wx, y: p.wy }, b = { x: q.wx, y: q.wy };
        const arc = CV && Math.abs(p.sag || 0) > 1e-9 ? CV.fitArc(a, b, p.sag) : null;
        if (!arc) { path.lineTo(b.x, b.y); continue; }
        const STEPS = 28;
        for (let s = 1; s <= STEPS; s++) {
          const pt = CV.pointAtParam(arc, a, b, s / STEPS);
          path.lineTo(pt.x, pt.y);
        }
      }
      return path;
    };

    if (poly.length >= 2) {
      const path = edgePath(this._calPolyClosed);
      ctx.strokeStyle = white; ctx.lineWidth = outerW; ctx.stroke(path);
      ctx.strokeStyle = blue;  ctx.lineWidth = innerW; ctx.stroke(path);
    }

    // Rubber-band from the last vertex to the cursor while still tracing. Inside the
    // close radius it SNAPS to the first vertex and turns solid green, so the line
    // itself shows you the loop will close — not just a dot changing colour.
    const willClose = !this._calPolyClosed && poly.length >= 3 && this._calMouseWorld &&
      Math.hypot(this._calMouseWorld.wx - poly[0].wx,
                 this._calMouseWorld.wy - poly[0].wy) <= this._calCloseRadius();
    if (!this._calPolyClosed && this._calMouseWorld && poly.length) {
      const lastV = poly[poly.length - 1];
      const to = willClose ? { wx: poly[0].wx, wy: poly[0].wy } : this._calMouseWorld;
      ctx.save();
      if (willClose) {
        ctx.setLineDash([]);
        ctx.strokeStyle = '#0a7d28';
        ctx.lineWidth = innerW * 1.6;
      } else {
        ctx.setLineDash([4 / scale, 4 / scale]);
        ctx.strokeStyle = blue;
        ctx.lineWidth = innerW;
      }
      ctx.beginPath();
      ctx.moveTo(lastV.wx, lastV.wy);
      ctx.lineTo(to.wx, to.wy);
      ctx.stroke();
      ctx.restore();
    }

    // Vertices. The first is the close target while the outline is open, and it goes
    // solid green only once the cursor is actually inside the snap radius — so the
    // highlight tells you the click will close rather than add another corner.
    // Vertices. The close target uses the pen tool's own snap marker — a filled
    // green dot, blue when out of range — rather than a bespoke ring, so closing a
    // traced outline looks the same as closing a room freehand.
    const canClose = !this._calPolyClosed && poly.length >= 3;
    poly.forEach((v, i) => {
      const isTarget = canClose && i === 0;
      if (isTarget) {
        const hot = willClose;
        const r = (hot ? 6 : 4) / scale;
        ctx.fillStyle = hot ? 'rgba(0, 200, 0, 0.7)' : 'rgba(0, 107, 204, 0.4)';
        ctx.beginPath(); ctx.arc(v.wx, v.wy, r, 0, Math.PI * 2); ctx.fill();
        ctx.strokeStyle = hot ? '#00aa00' : '#006BCC';
        ctx.lineWidth = 1.5 / scale;
        ctx.beginPath(); ctx.arc(v.wx, v.wy, r, 0, Math.PI * 2); ctx.stroke();
        return;
      }
      ctx.beginPath();
      ctx.arc(v.wx, v.wy, dotR, 0, Math.PI * 2);
      ctx.fillStyle = '#fff';
      ctx.fill();
      ctx.strokeStyle = blue;
      ctx.lineWidth = innerW;
      ctx.stroke();
    });

    // Bow handles once closed — drag one to curve that wall.
    if (this._calPolyClosed && poly.length >= 3) {
      const hR = 5 / scale;
      for (let i = 0; i < poly.length; i++) {
        const h = this._calBowHandle(i);
        if (!h) continue;
        const active = this._calBowDrag && this._calBowDrag.i === i;
        ctx.beginPath();
        ctx.arc(h.x, h.y, active ? hR * 1.35 : hR, 0, Math.PI * 2);
        ctx.fillStyle = '#fff';
        ctx.strokeStyle = active ? '#00aa00' : blue;
        ctx.lineWidth = (active ? 2.4 : 1.6) / scale;
        ctx.fill();
        ctx.stroke();
      }
    }
  }

  /** Signed perpendicular offset of a point from edge i's chord — the bow depth. */
  _calSagFromPoint(i, wx, wy) {
    const CV = window.RoomDesignerCurve;
    const poly = this._calPoly;
    if (!CV || !poly || poly.length < 3) return 0;
    const p = poly[i], q = poly[(i + 1) % poly.length];
    return CV.sagittaFrom({ x: p.wx, y: p.wy }, { x: q.wx, y: q.wy }, { x: wx, y: wy });
  }

  _hitTestCalBowHandle(wx, wy) {
    if (!this._calPolyClosed || !this._calPoly || this._calPoly.length < 3) return null;
    const r = this._calScreenR(9);
    for (let i = 0; i < this._calPoly.length; i++) {
      const h = this._calBowHandle(i);
      if (h && Math.hypot(wx - h.x, wy - h.y) <= r) return h;
    }
    return null;
  }

  /** Backspace during a trace removes the last vertex (or re-opens a closed outline). */
  _undoAreaTracePoint() {
    if (!this._calPoly || !this._calPoly.length) return false;
    if (this._calPolyClosed) {
      this._calPolyClosed = false;
      this._calStep = 1;
      this.rd.canvas.style.cursor = 'crosshair';
      const calSection = document.getElementById('bgCalInputSection');
      if (calSection) calSection.style.display = 'none';
      if (this._calApplyRow) { this._calApplyRow.remove(); this._calApplyRow = null; }
    } else {
      this._calPoly.pop();
    }
    this._showCalToast(1, this._calPoly.length >= 3
      ? 'Click the first point to close, or keep adding corners'
      : 'Trace the room — click each corner');
    this.rd.render();
    return true;
  }

  _startCalibration() {
    if (!this._bgImage) return;
    this._calibrating = true;
    this._calMode = 'distance';
    this._calStep = 1;
    this._calPoint1 = null;
    this._calPoint2 = null;
    this._calMouseWorld = null;
    this._deselectBg();
    this.rd.canvas.style.cursor = 'crosshair';
    this._clearCalBtnActive();
    const scl = document.getElementById('bgSetScaleBtn');
    if (scl) scl.classList.add('active');
    const statusEl = document.getElementById('bgScaleStatus');
    if (statusEl) statusEl.textContent = '';
    const calSection = document.getElementById('bgCalInputSection');
    if (calSection) calSection.style.display = 'none';
    this._showCalToast(1, 'Click the first point on a known measurement');
    this.rd.render();
  }

  _startLevel() {
    if (!this._bgImage) return;
    this._leveling = true;
    this._calibrating = true;
    this._calStep = 1;
    this._calPoint1 = null;
    this._calPoint2 = null;
    this._calMouseWorld = null;
    this._deselectBg();
    this.rd.canvas.style.cursor = 'crosshair';
    this._clearCalBtnActive();
    const lvl = document.getElementById('bgLevelBtn');
    if (lvl) lvl.classList.add('active');
    const calSection = document.getElementById('bgCalInputSection');
    if (calSection) calSection.style.display = 'none';
    this._showCalToast(1, 'Click the start of a line that should be horizontal');
    this.rd.render();
  }

  _cancelCalibration() {
    this._calibrating = false;
    this._leveling = false;
    // CUSTOM_ROOMDESIGNER_BY_CLAUDE
    this._calMode = 'distance';
    this._calPoly = [];
    this._calPolyClosed = false;
    this._calBowDrag = null;
    this._hideScaleMethods();
    // END CUSTOM_ROOMDESIGNER_BY_CLAUDE
    this._calStep = 0;
    this._calPoint1 = null;
    this._calPoint2 = null;
    this._calMouseWorld = null;
    this.rd.canvas.style.cursor = '';
    this._clearCalBtnActive();
    const statusEl = document.getElementById('bgScaleStatus');
    if (statusEl) {
      statusEl.textContent = this._bgScaleSet ? 'Scale set' : '';
    }
    const calSection = document.getElementById('bgCalInputSection');
    if (calSection) calSection.style.display = 'none';
    if (this._calApplyRow) { this._calApplyRow.remove(); this._calApplyRow = null; }
    this._hideCalToast();
    this.rd.render();
  }

  _handleCalibrationClick(wx, wy) {
    // CUSTOM_ROOMDESIGNER_BY_CLAUDE — area mode traces an outline instead of running
    // the two-point state machine.
    if (this._calMode === 'area') {
      if (!this._calPolyClosed) return this._handleAreaTraceClick(wx, wy);
      return false;   // closed — let the bow-drag handler have the event
    }
    // END CUSTOM_ROOMDESIGNER_BY_CLAUDE
    if (this._calStep === 1) {
      this._calPoint1 = { wx, wy };
      this._calStep = 2;
      this.rd.canvas.style.cursor = 'crosshair';
      this._showCalToast(2, this._leveling
        ? 'Click the end of the same line'
        : (this._calMode === 'area'
          ? 'Click the opposite corner of that room'   // CUSTOM_ROOMDESIGNER_BY_CLAUDE
          : 'Click the second point on the same measurement'));
      this.rd.render();
      return true;
    }
    if (this._calStep === 2) {
      this._calPoint2 = { wx, wy };
      this._calMouseWorld = null;

      // Level mode: apply rotation and finish (no distance input needed)
      if (this._leveling) {
        this._applyLevel();
        return true;
      }

      this._calStep = 3;
      this.rd.canvas.style.cursor = 'default';
      this._showCalToast(3, 'Enter the real-world distance in the popover');
      this._buildCalInputRow();
      const calSection = document.getElementById('bgCalInputSection');
      if (calSection) calSection.style.display = '';
      const firstInput = document.getElementById('bgCalDist1');
      if (firstInput) { firstInput.value = ''; firstInput.focus(); }
      this.rd.render();
      return true;
    }
    return false;
  }

  _buildCalInputRow() {
    const row = document.getElementById('bgCalInputRow');
    if (!row) return;
    const unit = this.rd.unit || 'feet-inches';
    const inputStyle = 'width:54px;font-size:12px;padding:4px 6px;border:1px solid #ccc;border-radius:4px;box-sizing:border-box;height:28px;';
    const suffixStyle = 'font-size:12px;color:#666;font-weight:600;min-width:12px;';
    let html = '';
    // CUSTOM_ROOMDESIGNER_BY_CLAUDE — area mode takes one plain square-footage value,
    // whatever display unit the rest of the app is in (drawings state areas in SF).
    const label = document.getElementById('bgCalInputLabel');
    if (this._calMode === 'area') {
      if (label) label.textContent = 'Stated area of that room';
      html = `<input type="number" id="bgCalDist1" min="1" step="any" style="${inputStyle}">` +
             `<span style="${suffixStyle}">sq ft</span>`;
    } else {
    if (label) label.textContent = 'Distance between points';
    // END CUSTOM_ROOMDESIGNER_BY_CLAUDE
    if (unit === 'feet-inches') {
      const fracStyle = 'width:58px;font-size:12px;padding:4px 6px;border:1px solid #ccc;border-radius:4px;box-sizing:border-box;height:28px;';
      html = `<input type="number" id="bgCalDist1" min="0" step="1" style="${inputStyle}">` +
             `<span style="${suffixStyle}">ft</span>` +
             `<input type="number" id="bgCalDist2" min="0" max="11" step="1" style="${inputStyle}">` +
             `<select id="bgCalFrac" style="${fracStyle}">` +
               `<option value="0">in</option>` +
               `<option value="0.125">⅛ in</option>` +
               `<option value="0.25">¼ in</option>` +
               `<option value="0.375">⅜ in</option>` +
               `<option value="0.5">½ in</option>` +
               `<option value="0.625">⅝ in</option>` +
               `<option value="0.75">¾ in</option>` +
               `<option value="0.875">⅞ in</option>` +
             `</select>`;
    } else if (unit === 'feet') {
      html = `<input type="number" id="bgCalDist1" min="0.1" step="any" style="${inputStyle}">` +
             `<span style="${suffixStyle}">ft</span>`;
    } else if (unit === 'inches') {
      html = `<input type="number" id="bgCalDist1" min="0.1" step="any" style="${inputStyle}">` +
             `<span style="${suffixStyle}">in</span>`;
    } else if (unit === 'meters') {
      html = `<input type="number" id="bgCalDist1" min="0.1" step="any" style="${inputStyle}">` +
             `<span style="${suffixStyle}">m</span>`;
    } else if (unit === 'm-cm') {
      html = `<input type="number" id="bgCalDist1" min="0" step="1" style="${inputStyle}">` +
             `<span style="${suffixStyle}">m</span>` +
             `<input type="number" id="bgCalDist2" min="0" max="99" step="1" style="${inputStyle}">` +
             `<span style="${suffixStyle}">cm</span>`;
    }
    } // CUSTOM_ROOMDESIGNER_BY_CLAUDE — end non-area branch; falls through to the
      // shared Apply button, which an early return here would have skipped.
    row.innerHTML = html;
    const applyRow = document.createElement('div');
    applyRow.style.cssText = 'display:flex;margin-top:6px;';
    applyRow.innerHTML = `<button class="pen-draw-mode-btn" id="bgCalApplyBtn" style="flex:1;justify-content:center;">Apply</button>`;
    row.parentNode.insertBefore(applyRow, row.nextSibling);
    this._calApplyRow = applyRow;
    document.getElementById('bgCalApplyBtn').addEventListener('click', () => this._applyCalibration());
    // Enter key applies from any input in the row
    row.querySelectorAll('input').forEach(inp => {
      inp.addEventListener('keydown', (e) => { if (e.key === 'Enter') this._applyCalibration(); });
    });
  }

  _applyCalibration() {
    const unit = this.rd.unit || 'feet-inches';
    const v1 = parseFloat(document.getElementById('bgCalDist1')?.value);
    const v2El = document.getElementById('bgCalDist2');
    const v2 = v2El ? parseFloat(v2El.value) : 0;

    let distFeet;
    if (unit === 'feet-inches') {
      const ft = v1 || 0, inches = v2 || 0;
      const fracEl = document.getElementById('bgCalFrac');
      const frac = fracEl ? parseFloat(fracEl.value) || 0 : 0;
      distFeet = ft + (inches + frac) / 12;
    } else if (unit === 'feet') {
      distFeet = v1;
    } else if (unit === 'inches') {
      distFeet = v1 / 12;
    } else if (unit === 'meters') {
      distFeet = v1 * 3.28084;
    } else if (unit === 'm-cm') {
      const m = v1 || 0, cm = v2 || 0;
      distFeet = (m + cm / 100) * 3.28084;
    }
    // CUSTOM_ROOMDESIGNER_BY_CLAUDE — the two-point guard applies to DISTANCE mode
    // only; a traced outline has no _calPoint1/2 at all, and this check silently
    // aborted every area calibration before it reached the maths.
    const isAreaMode = this._calMode === 'area';
    if (!isAreaMode && (!this._calPoint1 || !this._calPoint2)) return;

    const dx = isAreaMode ? 0 : this._calPoint2.wx - this._calPoint1.wx;
    const dy = isAreaMode ? 0 : this._calPoint2.wy - this._calPoint1.wy;

    let scaleFactor;
    // Area mode: the traced outline (bows included) is compared against the room's
    // stated area, so the scale is the square root of the AREA ratio, not a length
    // ratio.
    if (isAreaMode) {
      const CV = window.RoomDesignerCurve;
      const statedArea = v1;                       // always plain square feet
      const verts = (this._calPoly || []).map(p => ({ x: p.wx, y: p.wy, sag: p.sag || 0 }));
      const tracedArea = CV ? CV.polygonAreaWithBows(verts) : 0;
      if (!statedArea || statedArea <= 0 || !(tracedArea > 1e-6)) {
        this.rd.updateStatus('Trace a room outline and enter its area first.', { error: true });
        return;
      }
      scaleFactor = Math.sqrt(statedArea / tracedArea);
    } else {
      if (!distFeet || distFeet <= 0) return;
      const worldDist = Math.sqrt(dx * dx + dy * dy);
      if (worldDist < 0.001) return; // points too close
      // The ratio: how many feet in the real world per current world-foot on the image
      scaleFactor = distFeet / worldDist;
    }
    // END CUSTOM_ROOMDESIGNER_BY_CLAUDE

    // Rescale image around its center
    const cx = this._bgWorldX + this._bgWorldWidth / 2;
    const cy = this._bgWorldY + this._bgWorldHeight / 2;

    this._bgWorldWidth *= scaleFactor;
    this._bgWorldHeight *= scaleFactor;
    this._bgWorldX = cx - this._bgWorldWidth / 2;
    this._bgWorldY = cy - this._bgWorldHeight / 2;

    // Recompute calibration points in new scale (they move with the image)
    // Since we scaled around center, transform their positions too
    // CUSTOM_ROOMDESIGNER_BY_CLAUDE — area mode has no cal points, it has a traced
    // outline. Move that instead so it stays glued to the image for the frame or two
    // before it's discarded.
    if (this._calPoint1 && this._calPoint2) {
      this._calPoint1.wx = cx + (this._calPoint1.wx - cx) * scaleFactor;
      this._calPoint1.wy = cy + (this._calPoint1.wy - cy) * scaleFactor;
      this._calPoint2.wx = cx + (this._calPoint2.wx - cx) * scaleFactor;
      this._calPoint2.wy = cy + (this._calPoint2.wy - cy) * scaleFactor;
    }
    if (this._calPoly && this._calPoly.length) {
      for (const v of this._calPoly) {
        v.wx = cx + (v.wx - cx) * scaleFactor;
        v.wy = cy + (v.wy - cy) * scaleFactor;
        v.sag = (v.sag || 0) * scaleFactor;
      }
    }
    // END CUSTOM_ROOMDESIGNER_BY_CLAUDE

    // Store scale info
    this._bgScaleSet = true;
    this._bgPixelsPerFoot = this._bgNaturalWidth / this._bgWorldWidth;
    this._updatePaperCheck(); // CUSTOM_ROOMDESIGNER_BY_CLAUDE

    // End calibration
    this._calibrating = false;
    this._calStep = 0;
    this._calMouseWorld = null;
    // CUSTOM_ROOMDESIGNER_BY_CLAUDE — the traced outline was scaffolding; drop it
    this._calPoly = [];
    this._calPolyClosed = false;
    this._calBowDrag = null;
    this._calMode = 'distance';
    this._hideScaleMethods();
    // END CUSTOM_ROOMDESIGNER_BY_CLAUDE
    this._clearCalBtnActive();
    const statusEl = document.getElementById('bgScaleStatus');
    if (statusEl) statusEl.textContent = 'Scale set';
    const calSection = document.getElementById('bgCalInputSection');
    if (calSection) calSection.style.display = 'none';
    if (this._calApplyRow) { this._calApplyRow.remove(); this._calApplyRow = null; }
    this._hideCalToast();

    // Re-center the view on the image so it doesn't fly off-screen
    const rd = this.rd;
    const imgCenterX = this._bgWorldX + this._bgWorldWidth / 2;
    const imgCenterY = this._bgWorldY + this._bgWorldHeight / 2;
    const screenCenterX = rd.canvas.width / (2 * devicePixelRatio);
    const screenCenterY = rd.canvas.height / (2 * devicePixelRatio);
    const _ws = rd.worldToScreen(imgCenterX, imgCenterY);
    rd.panX += screenCenterX - _ws.x;
    rd.panY += screenCenterY - _ws.y;

    this._saveCurrent();
    rd.render();
  }

  _applyLevel() {
    if (!this._calPoint1 || !this._calPoint2) return;

    const dx = this._calPoint2.wx - this._calPoint1.wx;
    const dy = this._calPoint2.wy - this._calPoint1.wy;
    const angle = Math.atan2(dy, dx); // radians, -PI to PI

    // Determine if the line is closer to horizontal or vertical
    const absAngle = Math.abs(angle);
    let target;
    if (absAngle > Math.PI / 4 && absAngle < 3 * Math.PI / 4) {
      // Closer to vertical — snap to nearest ±90°
      target = angle > 0 ? Math.PI / 2 : -Math.PI / 2;
    } else {
      // Closer to horizontal — snap to 0 or ±180°
      target = absAngle > Math.PI / 2 ? (angle > 0 ? Math.PI : -Math.PI) : 0;
    }

    const correction = target - angle;
    const correctionDeg = correction * 180 / Math.PI;

    // Save undo state before applying
    this.rd.saveUndoState();

    // Apply rotation correction
    this._bgRotation += correction;
    // Normalize to [0, 2π)
    this._bgRotation = ((this._bgRotation % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);

    // Update rotation slider + number input
    const rotDeg = Math.round(this._bgRotation * 180 / Math.PI * 10) / 10;
    const rotSlider = document.getElementById('bgRotationSlider');
    const rotInput = document.getElementById('bgRotationInput');
    if (rotSlider) rotSlider.value = Math.round(rotDeg);
    if (rotInput) rotInput.value = Math.round(rotDeg * 10) / 10; // 0.1° precision

    // Save to array and exit calibration/leveling state
    this._saveCurrent();
    this._calibrating = false;
    this._leveling = false;
    this._calStep = 0;
    this._calMouseWorld = null;
    this.rd.canvas.style.cursor = '';
    this._clearCalBtnActive();

    // Show result toast with auto-fade
    const sign = correctionDeg >= 0 ? '+' : '';
    const roundedDeg = Math.round(correctionDeg * 10) / 10;
    this._showCalToast(null, 'Leveled \u2014 rotated ' + sign + roundedDeg + '\u00B0');
    setTimeout(() => this._hideCalToast(), 2000);

    this.rd.render();
  }

  // ─── Render Hook ──────────────────────────────────────────────────

  _installHook() {
    const self = this;
    const rd = this.rd;

    rd._drawBackgroundHook = function() {
      // Hide blueprint images in view-only mode
      if (rd._viewOnly) return;
      // Only draw in Draw tab
      const overlay = window.roomDesignerOverlay;
      if (overlay && overlay.currentStep !== 'draw') return;
      if (self._bgImages.length === 0) return;

      const ctx = rd.ctx;

      // Draw ALL images in order
      for (let i = 0; i < self._bgImages.length; i++) {
        const img = self._bgImages[i];
        if (!img.image || !img.visible) continue;

        ctx.save();
        const cx = img.worldX + img.worldWidth / 2;
        const cy = img.worldY + img.worldHeight / 2;
        ctx.globalAlpha = img.opacity;
        ctx.translate(cx, cy);
        ctx.rotate(img.rotation);
        ctx.drawImage(img.image, -img.worldWidth / 2, -img.worldHeight / 2, img.worldWidth, img.worldHeight);
        ctx.restore();
      }

      // Draw selection outline for selected image
      if (self._bgSelected && self._bgSelectedIdx >= 0) {
        self._drawSelectionOutline(ctx);
      }

      // Draw calibration markers
      if (self._calibrating) {
        self._drawCalibrationMarkers(ctx);
      }
    };
  }

  _drawSelectionOutline(ctx) {
    const rd = this.rd;
    const scale = rd.basePixelsPerFoot * rd.zoomLevel;

    ctx.save();
    const cx = this._bgWorldX + this._bgWorldWidth / 2;
    const cy = this._bgWorldY + this._bgWorldHeight / 2;
    ctx.translate(cx, cy);
    ctx.rotate(this._bgRotation);

    const hw = this._bgWorldWidth / 2;
    const hh = this._bgWorldHeight / 2;

    // Solid red outline (matches equipment selection style)
    ctx.lineWidth = 2 / scale;
    ctx.strokeStyle = 'rgba(220, 40, 40, 0.9)';
    ctx.strokeRect(-hw, -hh, this._bgWorldWidth, this._bgWorldHeight);

    ctx.restore();
  }

  _drawCalibrationMarkers(ctx) {
    const rd = this.rd;
    const scale = rd.basePixelsPerFoot * rd.zoomLevel;
    const blue = '#006BCC';
    const white = 'rgba(255,255,255,0.9)';

    // CUSTOM_ROOMDESIGNER_BY_CLAUDE — traced calibration outline
    if (this._calMode === 'area' && this._calPoly && this._calPoly.length) {
      this._drawCalTrace(ctx, scale, blue, white);
      return;
    }
    // END CUSTOM_ROOMDESIGNER_BY_CLAUDE

    // Crosshair arm length and line widths in world units
    const arm = 10 / scale;
    const outerW = 3.5 / scale;  // white halo
    const innerW = 1.5 / scale;  // blue core

    ctx.save();

    // Two-pass crosshair: white halo underneath, blue on top
    const drawCrosshair = (wx, wy) => {
      const path = new Path2D();
      path.moveTo(wx - arm, wy);
      path.lineTo(wx + arm, wy);
      path.moveTo(wx, wy - arm);
      path.lineTo(wx, wy + arm);
      ctx.setLineDash([]);
      ctx.lineWidth = outerW;
      ctx.strokeStyle = white;
      ctx.stroke(path);
      ctx.lineWidth = innerW;
      ctx.strokeStyle = blue;
      ctx.stroke(path);
    };

    if (this._calPoint1) drawCrosshair(this._calPoint1.wx, this._calPoint1.wy);
    if (this._calPoint2) drawCrosshair(this._calPoint2.wx, this._calPoint2.wy);

    // Two-pass dotted line: white halo underneath, blue on top
    const endPt = this._calPoint2 || this._calMouseWorld;
    if (this._calPoint1 && endPt) {
      const dash = [4 / scale, 4 / scale];
      const linePath = new Path2D();
      linePath.moveTo(this._calPoint1.wx, this._calPoint1.wy);
      linePath.lineTo(endPt.wx, endPt.wy);
      ctx.setLineDash(dash);
      ctx.lineWidth = outerW;
      ctx.strokeStyle = white;
      ctx.stroke(linePath);
      ctx.lineWidth = innerW;
      ctx.strokeStyle = blue;
      ctx.stroke(linePath);
      ctx.setLineDash([]);
    }

    ctx.restore();
  }

  // ─── Mouse/Touch Interception ─────────────────────────────────────

  _wrapMouseHandlers() {
    const rd = this.rd;
    const self = this;

    // Wrap _processDown (signature: sx, sy, world, snapped, button, shiftKey)
    const origProcessDown = rd._processDown.bind(rd);
    rd._processDown = function(sx, sy, world, snapped, button, shiftKey) {
      let _raw = rd.screenToWorld(sx, sy);
      let rawWX = _raw.x;
      let rawWY = _raw.y;

      // CUSTOM_ROOMDESIGNER_BY_CLAUDE — area trace: grab a bow handle on a closed
      // outline, otherwise keep dropping vertices.
      if (self._calibrating && self._calMode === 'area' && button === 0) {
        // Corners are draggable, so a mis-clicked one can be nudged instead of
        // restarting the trace. Checked before everything else, and before the
        // add-a-vertex path, so grabbing an existing corner never drops a new one.
        // Exception: the first corner while still open is the CLOSE target, not a
        // drag handle — that gesture has to keep working.
        const vi = self._hitTestCalVertex(rawWX, rawWY);
        const firstIsCloseTarget = !self._calPolyClosed && vi === 0 && self._calPoly.length >= 3;
        if (vi >= 0 && !firstIsCloseTarget) {
          self._calVertexDrag = { i: vi };
          self.rd.canvas.style.cursor = 'grabbing';
          return;
        }
        if (self._calPolyClosed) {
          const hit = self._hitTestCalBowHandle(rawWX, rawWY);
          if (hit) {
            self._calBowDrag = {
              i: hit.i,
              grabSag: self._calSagFromPoint(hit.i, rawWX, rawWY),
              baseSag: self._calPoly[hit.i].sag || 0
            };
            self.rd.canvas.style.cursor = 'grabbing';
            return;
          }
        } else if (self._handleCalibrationClick(rawWX, rawWY)) {
          return;
        }
      }
      // END CUSTOM_ROOMDESIGNER_BY_CLAUDE

      // Calibration takes highest priority
      if (self._calibrating && (self._calStep === 1 || self._calStep === 2) && button === 0) {
        // Apply shift constraint on 2nd click to match the rubber-band preview
        if (self._calStep === 2 && self._calPoint1 && shiftKey) {
          const dx = Math.abs(rawWX - self._calPoint1.wx);
          const dy = Math.abs(rawWY - self._calPoint1.wy);
          if (dx >= dy) rawWY = self._calPoint1.wy;
          else rawWX = self._calPoint1.wx;
        }
        if (self._handleCalibrationClick(rawWX, rawWY)) return;
      }

      // When blueprint popover is pinned, block all drawing on canvas
      // (image interactions still allowed below, but no room/wall drawing)
      const _bgPopPinned = rd._pinnedPopover === 'bgImage';

      // Draw tab + left click: check for image hits BEFORE room routing
      // so the user can always click on a background image even if rooms are on top.
      // Skip when mid-box-select (_selectCorner1) — let the 2nd click finalize normally.
      // However, walls/doors take priority — if a wall is under the cursor, let the
      // click through to normal handling so walls can be dragged over images.
      if (self._bgImages.length > 0 && !self._calibrating && !rd._selectCorner1 &&
          (rd.activeTool === 'select' || _bgPopPinned) && !rd._floorMode && !rd._equipmentMode && button === 0) {
        const hitIdx = self._hitTestAny(rawWX, rawWY);

        // Check if a wall/door is under the cursor — they take priority over images.
        // Also skip image interaction entirely if walls/doors are already selected,
        // so the user can't accidentally drag the blueprint while adjusting walls.
        const wallHit = rd._hitTestSelect ? rd._hitTestSelect({ x: rawWX, y: rawWY }) : null;
        const hasWallHit = wallHit && (wallHit.type === 'wall' || wallHit.type === 'door');
        const hasSelection = (rd.selectedWalls && rd.selectedWalls.size > 0) || (rd.selectedDoors && rd.selectedDoors.size > 0);
        if (hasSelection) {
          if (self._bgSelected) self._deselectBg();
          // Fall through to normal wall/door handling
        } else

        if (self._bgSelected && self._bgSelectedIdx >= 0) {
          // An image is already selected
          if (hitIdx === self._bgSelectedIdx && !hasWallHit) {
            // Clicked on the same image (no wall underneath) → start drag
            self._draggingBg = true;
            self._dragStartWorld = { wx: rawWX, wy: rawWY };
            self._dragStartBgPos = { x: self._bgWorldX, y: self._bgWorldY };
            rd.canvas.style.cursor = 'grabbing';
            return;
          } else if (hitIdx >= 0 && !hasWallHit) {
            // Clicked on a different image (no wall underneath) → switch to it and start drag
            self._saveCurrent();
            self._loadImage(hitIdx);
            self._bgSelected = true;
            self._draggingBg = true;
            self._dragStartWorld = { wx: rawWX, wy: rawWY };
            self._dragStartBgPos = { x: self._bgWorldX, y: self._bgWorldY };
            self._updatePopoverUI();
            rd.canvas.style.cursor = 'grabbing';
            if (rd._updateFloatingDelete) rd._updateFloatingDelete();
            rd.render();
            return;
          } else {
            // Clicked outside all images OR on a wall → deselect, fall through to normal routing
            self._deselectBg();
          }
        } else if (hitIdx >= 0 && !hasWallHit) {
          // No image selected yet, clicked on image (no wall underneath) → select + drag it
          self._saveCurrent();
          self._loadImage(hitIdx);
          self._bgSelected = true;
          self._draggingBg = true;
          self._dragStartWorld = { wx: rawWX, wy: rawWY };
          self._dragStartBgPos = { x: self._bgWorldX, y: self._bgWorldY };
          self._updatePopoverUI();
          self._showControls(true);
          rd.canvas.style.cursor = 'grabbing';
          if (rd._updateFloatingDelete) rd._updateFloatingDelete();
          rd.render();
          return;
        }
      }

      // If blueprint popover is pinned, block all drawing (only image interactions above)
      if (_bgPopPinned) return;

      origProcessDown(sx, sy, world, snapped, button, shiftKey);
    };

    // Wrap _processMove (signature: sx, sy) for calibration live line + background drag
    const origProcessMove = rd._processMove.bind(rd);
    rd._processMove = function(sx, sy) {
      // CUSTOM_ROOMDESIGNER_BY_CLAUDE — area trace: bow an edge, or rubber-band the
      // next segment while the outline is still open.
      if (self._calibrating && self._calMode === 'area') {
        const _m = rd.screenToWorld(sx, sy);
        if (self._calVertexDrag) {
          const v = self._calPoly[self._calVertexDrag.i];
          if (v) { v.wx = _m.x; v.wy = _m.y; }
          rd.render();
          return;
        }
        if (self._calBowDrag) {
          const d = self._calBowDrag;
          const now = self._calSagFromPoint(d.i, _m.x, _m.y);
          if (rd.shiftHeld) {
            // Same as the wall bulge handle: Shift is a magnet back to straight,
            // re-baselined each frame so releasing it resumes from flat.
            self._calPoly[d.i].sag = 0;
            d.baseSag = 0;
            d.grabSag = now;
          } else {
            self._calPoly[d.i].sag = d.baseSag + (now - d.grabSag);
          }
          rd.render();
          return;
        }
        if (!self._calPolyClosed) {
          self._calMouseWorld = { wx: _m.x, wy: _m.y };
          rd.render();
          return;
        }
        // Corners are draggable too, so they need the same grab affordance.
        const overVert = self._hitTestCalVertex(_m.x, _m.y) >= 0;
        rd.canvas.style.cursor =
          (overVert || self._hitTestCalBowHandle(_m.x, _m.y)) ? 'grab' : 'default';
        return;
      }
      // END CUSTOM_ROOMDESIGNER_BY_CLAUDE
      // During calibration, skip normal move entirely — just track cursor for rubber-band
      if (self._calibrating && (self._calStep === 1 || self._calStep === 2)) {
        if (self._calStep === 2 && self._calPoint1) {
          let _rawM = rd.screenToWorld(sx, sy);
          let rawWX = _rawM.x;
          let rawWY = _rawM.y;
          // Shift constrains to horizontal or vertical from point 1
          if (rd.shiftHeld) {
            const dx = Math.abs(rawWX - self._calPoint1.wx);
            const dy = Math.abs(rawWY - self._calPoint1.wy);
            if (dx >= dy) rawWY = self._calPoint1.wy;
            else rawWX = self._calPoint1.wx;
          }
          self._calMouseWorld = { wx: rawWX, wy: rawWY };
          rd.render();
        }
        rd.canvas.style.cursor = 'crosshair';
        return;
      }

      if (self._draggingBg) {
        const _rawD = rd.screenToWorld(sx, sy);
        const rawWX = _rawD.x;
        const rawWY = _rawD.y;
        self._bgWorldX = self._dragStartBgPos.x + (rawWX - self._dragStartWorld.wx);
        self._bgWorldY = self._dragStartBgPos.y + (rawWY - self._dragStartWorld.wy);
        self._saveCurrent();
        rd.render();
        return;
      }
      origProcessMove(sx, sy);
    };

    // Wrap _processUp (signature: no args)
    const origProcessUp = rd._processUp.bind(rd);
    rd._processUp = function() {
      // CUSTOM_ROOMDESIGNER_BY_CLAUDE — finish a corner or bow drag on the outline
      if (self._calVertexDrag || self._calBowDrag) {
        self._calVertexDrag = null;
        self._calBowDrag = null;
        self.rd.canvas.style.cursor = 'default';
        self.rd.render();
        return;
      }
      // END CUSTOM_ROOMDESIGNER_BY_CLAUDE
      if (self._draggingBg) {
        // Save undo state if the image actually moved
        const dx = self._bgWorldX - self._dragStartBgPos.x;
        const dy = self._bgWorldY - self._dragStartBgPos.y;
        if (Math.abs(dx) > 0.01 || Math.abs(dy) > 0.01) {
          // Temporarily restore pre-drag position so saveUndoState snapshots it
          const newX = self._bgWorldX, newY = self._bgWorldY;
          self._bgWorldX = self._dragStartBgPos.x;
          self._bgWorldY = self._dragStartBgPos.y;
          self._saveCurrent();
          rd.saveUndoState();
          // Re-apply the new position
          self._bgWorldX = newX;
          self._bgWorldY = newY;
          self._saveCurrent();
        }
        self._draggingBg = false;
        self._dragStartWorld = null;
        self._dragStartBgPos = null;
        // Stay selected so user can grab again; show grab cursor
        rd.canvas.style.cursor = self._bgSelected ? 'grab' : '';
        rd.render();
        return;
      }
      origProcessUp();
    };

    // Also handle touch for calibration on mobile
    const origTouchStart = rd._handleTouchStart ? rd._handleTouchStart.bind(rd) : null;
    if (origTouchStart) {
      rd._handleTouchStart = function(e) {
        if (self._calibrating && (self._calStep === 1 || self._calStep === 2) && e.touches.length === 1) {
          const rect = rd.canvas.getBoundingClientRect();
          const sx = e.touches[0].clientX - rect.left;
          const sy = e.touches[0].clientY - rect.top;
          const _rawT = rd.screenToWorld(sx, sy);
          const rawWX = _rawT.x;
          const rawWY = _rawT.y;
          if (self._handleCalibrationClick(rawWX, rawWY)) {
            e.preventDefault();
            return;
          }
        }
        origTouchStart(e);
      };
    }
  }

  _hitTestImage(wx, wy) {
    if (!this._bgImage || !this._bgVisible) return false;

    // Transform world point to image-local coords (accounting for rotation)
    const cx = this._bgWorldX + this._bgWorldWidth / 2;
    const cy = this._bgWorldY + this._bgWorldHeight / 2;
    const dx = wx - cx;
    const dy = wy - cy;

    // Rotate point by -rotation to get to image-local space
    const cos = Math.cos(-this._bgRotation);
    const sin = Math.sin(-this._bgRotation);
    const lx = dx * cos - dy * sin;
    const ly = dx * sin + dy * cos;

    const hw = this._bgWorldWidth / 2;
    const hh = this._bgWorldHeight / 2;
    return lx >= -hw && lx <= hw && ly >= -hh && ly <= hh;
  }

  // ─── Tool Switch ───────────────────────────────────────────────────

  _wrapToolSwitch() {
    const rd = this.rd;
    const self = this;
    const origSetTool = rd._doSetActiveTool.bind(rd);
    rd._doSetActiveTool = function(tool) {
      // Keep calibration line for passive tools; clear for drawing/measure tools
      const keepCal = tool === 'select' || tool === 'hand' || tool === 'eraser';
      if (self._calibrating && !keepCal) self._cancelCalibration();
      self._deselectBg();
      origSetTool(tool);
    };

    // setMode('door') is a separate code path for Place Objects — doesn't go through _doSetActiveTool
    const origSetMode = rd.setMode.bind(rd);
    rd.setMode = function(mode) {
      if (self._calibrating) self._cancelCalibration();
      self._deselectBg();
      origSetMode(mode);
    };
  }

  // ─── Box Select ──────────────────────────────────────────────────

  _wrapBoxSelect() {
    const rd = this.rd;
    const self = this;
    const origUpdateSel = rd._updateSelection.bind(rd);
    rd._updateSelection = function() {
      origUpdateSel();
      // After normal selection, check if any background images overlap the box
      if (!rd.selectionStart || !rd.selectionEnd || rd._floorMode || rd._equipmentMode) return;
      if (self._bgImages.length === 0) return;
      const minX = Math.min(rd.selectionStart.x, rd.selectionEnd.x);
      const maxX = Math.max(rd.selectionStart.x, rd.selectionEnd.x);
      const minY = Math.min(rd.selectionStart.y, rd.selectionEnd.y);
      const maxY = Math.max(rd.selectionStart.y, rd.selectionEnd.y);
      // Select topmost visible image whose center falls inside the box
      for (let i = self._bgImages.length - 1; i >= 0; i--) {
        const img = self._bgImages[i];
        if (!img.image || !img.visible) continue;
        const cx = img.worldX + img.worldWidth / 2;
        const cy = img.worldY + img.worldHeight / 2;
        if (cx >= minX && cx <= maxX && cy >= minY && cy <= maxY) {
          self._saveCurrent();
          self._loadImage(i);
          self._bgSelected = true;
          self._updatePopoverUI();
          self._showControls(true);
          if (rd._updateFloatingDelete) rd._updateFloatingDelete();
          rd.render();
          break;
        }
      }
    };
  }

  // ─── Undo / Redo ────────────────────────────────────────────────

  _snapshotImages() {
    this._saveCurrent();
    return this._bgImages.map(img => ({
      id: img.id, imageData: img.imageData,
      naturalWidth: img.naturalWidth, naturalHeight: img.naturalHeight,
      worldX: img.worldX, worldY: img.worldY,
      worldWidth: img.worldWidth, worldHeight: img.worldHeight,
      rotation: img.rotation, opacity: img.opacity, visible: img.visible,
      scaleSet: img.scaleSet, pixelsPerFoot: img.pixelsPerFoot
    }));
  }

  _restoreImages(snapshot) {
    if (!snapshot) return;
    const self = this;
    this._bgSelected = false;
    this._draggingBg = false;
    this._calibrating = false;
    this._leveling = false;
    this._calStep = 0;
    this._bgImages = snapshot.map(s => {
      const entry = Object.assign({}, s, { image: null });
      if (s.imageData) {
        const im = new Image();
        im.onload = () => {
          entry.image = im;
          // Keep mirror in sync if this entry is currently selected
          if (self._bgSelectedIdx >= 0 && self._bgImages[self._bgSelectedIdx] === entry) {
            self._bgImage = im;
          }
          self._syncTraceFlag();
          self.rd.render();
        };
        im.src = s.imageData;
      }
      return entry;
    });
    this._nextBgId = this._bgImages.reduce((mx, i) => Math.max(mx, (i.id || 0) + 1), 1);
    if (this._bgImages.length > 0) {
      this._loadImage(0);
      this._showControls(true);
    } else {
      this._loadImage(-1);
      this._showControls(false);
    }
    this._updatePopoverUI();
    this._syncTraceFlag();
    this._hideCalToast();
    const calSection = document.getElementById('bgCalInputSection');
    if (calSection) calSection.style.display = 'none';
  }

  _wrapUndoRedo() {
    const rd = this.rd;
    const self = this;

    // Parallel bg stacks — kept in sync with main undo/redo stacks
    const origSave = rd.saveUndoState.bind(rd);
    this._bgUndoStack = [];
    this._bgRedoStack = [];

    rd.saveUndoState = function() {
      self._bgUndoStack.push(self._snapshotImages());
      if (self._bgUndoStack.length > 50) self._bgUndoStack.shift();
      self._bgRedoStack = [];
      origSave();
    };

    const origUndo2 = rd.undo.bind(rd);
    rd.undo = function() {
      // During calibration, undo steps backward instead of popping the main stack
      if (self._calibrating) {
        if (self._calStep === 3) {
          self._calPoint2 = null;
          self._calStep = 2;
          rd.canvas.style.cursor = 'crosshair';
          const calSection = document.getElementById('bgCalInputSection');
          if (calSection) calSection.style.display = 'none';
          self._showCalToast(2, self._leveling
            ? 'Click the end of the same line'
            : 'Click the second point on the same measurement');
          rd.render();
        } else {
          // Step 2 or 1 → cancel entirely (restores default cursor)
          self._cancelCalibration();
        }
        return;
      }
      const prevLen = rd.undoStack.length;
      origUndo2();
      // Only manage bg stack when the main undo stack actually popped
      // (skip door-mobile phase undo, object preview cancel, etc.)
      if (rd.undoStack.length < prevLen && self._bgUndoStack.length > 0) {
        self._bgRedoStack.push(self._snapshotImages());
        self._restoreImages(self._bgUndoStack.pop());
      }
      if (rd._updateFloatingDelete) rd._updateFloatingDelete();
    };

    const origRedo = rd.redo.bind(rd);
    rd.redo = function() {
      const prevLen = rd.redoStack.length;
      origRedo();
      // Only manage bg stack when the main redo stack actually popped
      if (rd.redoStack.length < prevLen && self._bgRedoStack.length > 0) {
        self._bgUndoStack.push(self._snapshotImages());
        self._restoreImages(self._bgRedoStack.pop());
      }
      if (rd._updateFloatingDelete) rd._updateFloatingDelete();
    };
  }

  // ─── Delete ─────────────────────────────────────────────────────

  _wrapDelete() {
    const rd = this.rd;
    const self = this;

    // Wrap _updateFloatingDelete to also show delete button when bg image selected
    const origUpdateDel = rd._updateFloatingDelete ? rd._updateFloatingDelete.bind(rd) : null;
    rd._updateFloatingDelete = function() {
      if (origUpdateDel) origUpdateDel();
      // If bg image is selected, force delete button visible
      if (self._bgSelected && self._bgSelectedIdx >= 0) {
        if (rd._floatingDeleteBtn) rd._floatingDeleteBtn.classList.add('visible');
        if (rd._desktopDeleteBtn) rd._desktopDeleteBtn.classList.add('visible');
      }
    };

    // Wrap deleteSelected to handle bg image deletion
    const origDeleteSel = rd.deleteSelected.bind(rd);
    rd.deleteSelected = function(opts) {
      if (self._bgSelected && self._bgSelectedIdx >= 0) {
        if (!opts || !opts.skipUndo) rd.saveUndoState();
        self._removeImage();
        if (rd._updateFloatingDelete) rd._updateFloatingDelete();
        rd._updateSelectionStatus();
        rd.render();
        return;
      }
      origDeleteSel(opts);
    };
  }

  // ─── Keyboard ─────────────────────────────────────────────────────

  _setupKeyboard() {
    const self = this;
    // Use capture phase so Escape reaches us BEFORE the main app's handler
    document.addEventListener('keydown', (e) => {
      // Escape cancels calibration or deselects background
      // CUSTOM_ROOMDESIGNER_BY_CLAUDE — while tracing a calibration outline:
      // Backspace undoes the last corner, Enter closes it.
      if (self._calibrating && self._calMode === 'area' &&
          !(e.target && /^(INPUT|SELECT|TEXTAREA)$/.test(e.target.tagName))) {
        if (e.key === 'Backspace') {
          e.preventDefault(); e.stopPropagation();
          self._undoAreaTracePoint();
          return;
        }
        if (e.key === 'Enter' && !self._calPolyClosed && self._calPoly.length >= 3) {
          e.preventDefault(); e.stopPropagation();
          self._closeAreaTrace();
          return;
        }
      }
      // END CUSTOM_ROOMDESIGNER_BY_CLAUDE
      if (e.key === 'Escape' && (self._calibrating || self._bgSelected)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        if (self._calibrating) self._cancelCalibration();
        if (self._bgSelected) self._deselectBg();
        return;
      }
      // Ctrl+Z during calibration: step backward (undo points one at a time)
      if ((e.ctrlKey || e.metaKey) && (e.key === 'z' || e.key === 'Z') && !e.shiftKey && self._calibrating) {
        e.preventDefault();
        e.stopImmediatePropagation();
        if (self._calStep === 3) {
          // Undo point 2 → go back to step 2
          self._calPoint2 = null;
          self._calStep = 2;
          self.rd.canvas.style.cursor = 'crosshair';
          const calSection = document.getElementById('bgCalInputSection');
          if (calSection) calSection.style.display = 'none';
          self._showCalToast(2, self._leveling
            ? 'Click the end of the same line'
            : 'Click the second point on the same measurement');
          self.rd.render();
        } else if (self._calStep === 2) {
          // Undo point 1 → cancel calibration entirely
          self._cancelCalibration();
        } else {
          // Step 1 with nothing placed → cancel entirely
          self._cancelCalibration();
        }
        return;
      }

      // Delete/Backspace deletes selected background image
      if ((e.key === 'Delete' || e.key === 'Backspace') && self._bgSelected && self._bgSelectedIdx >= 0) {
        const tag = document.activeElement?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
        e.preventDefault();
        e.stopPropagation();
        self.rd.saveUndoState();
        self._removeImage();
        if (self.rd._updateFloatingDelete) self.rd._updateFloatingDelete();
        self.rd.render();
        return;
      }

      // Arrow keys nudge selected background image (1 inch, or 1 foot with Shift)
      if ((e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight') && self._bgSelected && self._bgSelectedIdx >= 0) {
        const tag = document.activeElement?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
        e.preventDefault();
        e.stopPropagation();
        const step = e.shiftKey ? 1 : (1 / 12); // 1 foot or 1 inch
        let dx = 0, dy = 0;
        if (e.key === 'ArrowLeft') dx = -step;
        else if (e.key === 'ArrowRight') dx = step;
        else if (e.key === 'ArrowUp') dy = -step;
        else if (e.key === 'ArrowDown') dy = step;
        self._bgWorldX += dx;
        self._bgWorldY += dy;
        self._saveCurrent();
        self.rd.render();
        return;
      }

      // B key shortcut removed (background popover accessible via toolbar)
    }, true); // capture phase — runs before main app's keydown handler
  }

  // ─── Serialization ────────────────────────────────────────────────

  _wrapSerialization() {
    const rd = this.rd;
    const self = this;

    // Wrap _serializeDesign to include background state
    // Blueprint images are excluded from saves (base64 data is too large for
    // localStorage / server payloads). Position metadata is kept so we know
    // blueprints were present, but they must be re-uploaded after loading.
    const origSerialize = rd._serializeDesign.bind(rd);
    rd._serializeDesign = function() {
      const data = origSerialize();
      self._saveCurrent(); // ensure array is up to date
      if (self._bgImages.length > 0) {
        data._backgroundsStripped = true;
        data._backgroundCount = self._bgImages.length;
      }
      return data;
    };

    // Wrap _loadDesignData to restore background
    const origLoad = rd._loadDesignData.bind(rd);
    rd._loadDesignData = function(data) {
      origLoad(data);

      // Backward compat: old single-image format → wrap in array
      let bgArray = data._backgrounds;
      if (!bgArray && data._background && data._background.imageData) {
        bgArray = [data._background];
      }
      // Filter out entries without imageData (stripped saves)
      if (bgArray) bgArray = bgArray.filter(bg => bg.imageData);

      if (bgArray && bgArray.length > 0) {
        self._bgImages = [];
        self._bgSelectedIdx = -1;
        let loadedCount = 0;
        const total = bgArray.length;

        bgArray.forEach((bg, i) => {
          const entry = {
            id: self._nextBgId++,
            image: null, imageData: bg.imageData,
            naturalWidth: bg.naturalWidth, naturalHeight: bg.naturalHeight,
            worldX: bg.worldX, worldY: bg.worldY,
            worldWidth: bg.worldWidth, worldHeight: bg.worldHeight,
            rotation: bg.rotation || 0,
            opacity: bg.opacity != null ? bg.opacity : 1.0,
            visible: bg.visible != null ? bg.visible : true,
            scaleSet: bg.scaleSet || false,
            pixelsPerFoot: bg.pixelsPerFoot || 0
          };
          self._bgImages.push(entry);

          const img = new Image();
          img.onload = () => {
            entry.image = img;
            loadedCount++;
            if (loadedCount === total) {
              // All images loaded — select the first one
              self._loadImage(0);
              self._showControls(true);
              self._updatePopoverUI();
              self._syncTraceFlag();
              rd.render();
            }
          };
          img.onerror = () => {
            console.warn('Failed to load floor plan image', i);
            loadedCount++;
          };
          img.src = bg.imageData;
        });
      } else {
        // No background in loaded data — clear all
        self._bgImages = [];
        self._bgSelectedIdx = -1;
        self._loadImage(-1);
        self._showControls(false);
        self._syncTraceFlag();
      }
    };
  }
}

/* END CUSTOM_PLANNER_BY_CLAUDE */

// ─── Instantiation ────────────────────────────────────────────────
document.addEventListener('DOMContentLoaded', () => {
  if (window.roomDesigner) {
    window.roomDesignerBackground = new RoomDesignerBackground(window.roomDesigner);
  }
});

